import { EventEmitter } from 'events';
import Environment from './Environment';
import { SessionState } from './Enums';
import Reason from './Reasons/Reason';
import SessionAPI from './API/SessionAPI';

export default class SessionService extends EventEmitter {
    /**
     * Session service constructor
     * @param {SessionAPI} sessionApi
     * @param {ProxyCollectionSdk} sdk
     * @param {Environment} environment
     */
    constructor(sessionGuid, sessionApi, sdk, environment, options) {
        super();

        this.sessionApi = sessionApi;
        this.sdk = sdk;
        this.environment = environment;

        this.sessionId = null;
        this.sessionGuid = sessionGuid;
        this.lastQueuedSessionStateId = 0;
        this.sessionStateId = 0;

        this.accumulatedLogs = [];
        this.options = options;
    }

    async createSession(participantId, urlParams, inclusionMode, customParticipantId) {
        const { identityProviderId, isReviewer, sampleProviderId, dataCollectionHash } = this.options;
        const createSessionResult = await this.sessionApi.createSession(dataCollectionHash, identityProviderId, participantId, navigator.userAgent,
            window.jsVersion, window.assemblyVersion, isReviewer, sampleProviderId, urlParams, this.sessionGuid,
            inclusionMode, this.environment.platformType, customParticipantId);

        if (createSessionResult.succeeded) {
            const resultData = createSessionResult.data;

            this.sessionId = resultData.sessionID;
            this.lastSessionStateId = resultData.sessionStateID;

            resultData.sessionGuid = this.sessionGuid;

            this.emit('sessionCreated', resultData);
        }

        return createSessionResult;
    }

    async updateSession(updateData, broadcastSessionState = true) {
        // save logs
        await this.logMessages(updateData.logs);

        // update session
        if (updateData.data) {
            // This is update session operation.
            await this.sessionApi.updateSession(this.sessionId, updateData.data);
        } else if (updateData.stateId) {
            await this.updateState(updateData);
        } else if (updateData.recordingEngine || updateData.testElementSequenceIds || updateData.postActionUrl) {
            await this.sessionApi.updateSession(this.sessionId, updateData);
        }

        if (broadcastSessionState) {
            this.broadcastSessionState(updateData);
        }
    }

    async changeState(stateId, reason, message, data) {
        if (!reason) {
            reason = new Reason(-1, 'message for reason');
        }

        const { declined, fail } = SessionState;
        if (this.lastSessionStateId != declined && this.lastSessionStateId != fail) {
            await this.updateSession({
                stateId: stateId,
                reason: reason,
                additionalData: data,
                logs: [{
                    logType: 'info',
                    logSource: 'DC js',
                    logMessage: message ?? reason?._messageKey
                }]
            });
        } else {
            await this.logMessage('error', 'Session already in state declined or fail');
        }
    }

    async updateState(updateData) {
        this.lastQueuedSessionStateId = updateData.stateId;

        const result = await this.sessionApi.setState(this.sessionId, updateData.reason, this.lastSessionStateId, updateData.stateId, new Date(), updateData.reasonMessage, updateData.reasonData, null, updateData.additionalData);

        if (result.succeeded) {
            this.lastSessionStateId = updateData.stateId;
            this.lastReason = updateData.reason;
        }
        else {
            this.lastSessionStateId = SessionState.fail;

            await this.sdk.logMessage(this.sessionId, null, 'error', new Date(), result.errorMessage);

            this.emit('sessionUpdateFailed', result);
        }
    }

    async logMessages(logs) {
        if (logs && logs.length) {
            const accumulatedLogs = logs.map(log => ({
                sessionId: this.sessionId,
                logSource: (log.logSource || 'DC js'),
                logType: log.logType,
                clientTime: (log.clientTime || new Date()),
                logMessage: log.logMessage
            }));

            // if we don't have a session, store logs
            if (this.sessionId == null) {
                this.accumulatedLogs = accumulatedLogs;
            } else {
                if (this.accumulatedLogs.length > 0) {
                    accumulatedLogs.push(this.accumulatedLogs);

                    this.accumulatedLogs = [];
                }

                //NOTE: if we are running in integration then we should send the log info to Integration, and it will log it with correct sequence numbers
                if (window.self !== window.top) {
                    this.emit('sessionLogged', { data: accumulatedLogs, onlyLog: true });
                } else {
                    await this.sdk.logMessages(accumulatedLogs);
                }
            }
        }
    }

    async logMessage(logType, logMessage, clientTime) {
        await this.logMessages([{ logSource: 'js', logType: logType, logMessage: logMessage, clientTime: clientTime }]);
    }

    async saveSessionResults(data) {
        await this.sessionApi.saveSessionResults(this.sessionId, data);
    }

    async getTestElementSequences() {
        return await this.sessionApi.getTestElementSequences(this.sessionId);
    }

    saveSessionData(data) {
        this.sessionApi.saveSessionData(this.sessionId, data);
    }

    broadcastSessionState(updateData) {
        if (!window.parent) {
            return;
        }

        const broadcastableStates = [
            { id: 13, name: 'Collected' },
            { id: 14, name: 'Declined' },
            { id: 15, name: 'Failed' }
        ];

        const message = broadcastableStates.find((e) => updateData.stateId === e.id);

        if (message) {
            message.reason = updateData.reasonMessage;

            this.emit('sessionStateChanged', message);
        }
    }
}